import * as Sentry from '@sentry/vue'
import { useToast } from 'primevue/usetoast'

/**
 * Composable pour gérer les erreurs de manière centralisée
 * Envoie automatiquement à Sentry + affiche un toast à l'utilisateur
 */
export function useErrorHandler() {
  const toast = useToast()

  /**
   * Capture une erreur et l'envoie à Sentry avec contexte
   * 
   * @param error - L'erreur à capturer
   * @param context - Contexte métier (module, action, données)
   * @param userMessage - Message à afficher à l'utilisateur (optionnel)
   * 
   * @example
   * try {
   *   await loadData()
   * } catch (error) {
   *   handleError(error, {
   *     module: 'Transparisation',
   *     action: 'loadInventory',
   *     perimeterId: 123
   *   }, 'Impossible de charger l\'inventaire')
   * }
   */
  function handleError(
    error: unknown,
    context?: {
      module?: string
      action?: string
      [key: string]: any
    },
    userMessage?: string
  ): void {
    // Log console en développement
    console.error('Error:', error, 'Context:', context)

    // Envoyer à Sentry avec contexte enrichi
    Sentry.captureException(error, {
      tags: {
        module: context?.module || 'unknown',
        action: context?.action || 'unknown',
      },
      contexts: {
        business: context || {},
      },
    })

    // Afficher toast utilisateur
    const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue'
    
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: userMessage || errorMessage,
      life: 5000,
    })
  }

  /**
   * Capture un avertissement (non bloquant)
   * 
   * @param message - Message d'avertissement
   * @param context - Contexte métier
   * 
   * @example
   * handleWarning('Données incomplètes détectées', {
   *   module: 'Perimeter',
   *   missingFields: ['compartments']
   * })
   */
  function handleWarning(
    message: string,
    context?: Record<string, any>
  ): void {
    console.warn('Warning:', message, 'Context:', context)

    Sentry.captureMessage(message, {
      level: 'warning',
      tags: {
        module: context?.module || 'unknown',
      },
      contexts: {
        business: context || {},
      },
    })

    toast.add({
      severity: 'warn',
      summary: 'Attention',
      detail: message,
      life: 3000,
    })
  }

  /**
   * Ajoute du contexte utilisateur à Sentry
   * À appeler après authentification
   * 
   * @param user - Informations utilisateur
   * 
   * @example
   * setUserContext({
   *   id: account.localAccountId,
   *   email: account.username,
   *   rolename: 'Client.EXAMPLE'
   * })
   */
  function setUserContext(user: {
    id?: string
    email?: string
    rolename?: string
    [key: string]: any
  }): void {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.rolename,
      ...user,
    })
  }

  /**
   * Ajoute du contexte métier global
   * 
   * @param key - Clé du contexte
   * @param data - Données du contexte
   * 
   * @example
   * setBusinessContext('perimeter', {
   *   client: 'Client.EXAMPLE',
   *   date: '2025-11-14'
   * })
   */
  function setBusinessContext(key: string, data: Record<string, any>): void {
    Sentry.setContext(key, data)
  }

  /**
   * Wrapper pour les actions async avec gestion d'erreur automatique
   * 
   * @param action - Action à exécuter
   * @param context - Contexte métier
   * @param userMessage - Message d'erreur utilisateur
   * @returns Résultat de l'action ou undefined en cas d'erreur
   * 
   * @example
   * const result = await withErrorHandling(
   *   () => api.loadData(),
   *   { module: 'API', action: 'loadData' },
   *   'Impossible de charger les données'
   * )
   */
  async function withErrorHandling<T>(
    action: () => Promise<T>,
    context?: { module?: string; action?: string; [key: string]: any },
    userMessage?: string
  ): Promise<T | undefined> {
    try {
      return await action()
    } catch (error) {
      handleError(error, context, userMessage)
      return undefined
    }
  }

  return {
    handleError,
    handleWarning,
    setUserContext,
    setBusinessContext,
    withErrorHandling,
  }
}
